import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { getLocationImage, getLocationDrawImages, getPlaceDrawDetails } from 'tripPlanner/api'
import type { LocationDrawImagesRequest } from 'tripPlanner/api/types'
import * as LocationImageKeys from '../reactQueryKeys/locationImage'

export type ActivityLocationImage = {
  id: string
  name: string
  placeId: string
  imageIds: Array<string>
}

export const useLocationImage = (
  placeId: string | undefined,
): UseQueryResult<Array<ActivityLocationImage>> => {
  return useQuery({
    queryKey: LocationImageKeys.locationImage(placeId || ''),
    queryFn: () => getLocationImage(placeId || ''),
    enabled: !!placeId,
  })
}

export type LocationDrawImagesData = LocationDrawImagesRequest | undefined
export type DrawImagesCategory = 'locations' | 'places'

export type LocationDrawImagesResult = {
  imageIdentifiers: Array<string>
  rating?: number
  userRatingCount?: number
  category?: string
}

export const useLocationDrawPlaceDetails = (
  locationData: LocationDrawImagesData,
  category: DrawImagesCategory = 'locations',
): UseQueryResult<LocationDrawImagesResult> => {
  return useQuery({
    queryKey: LocationImageKeys.locationDrawImages(
      category,
      locationData?.title ?? '',
      locationData?.description,
      locationData?.emoji,
      locationData?.placeSearch,
    ),
    queryFn: async(): Promise<LocationDrawImagesResult> => {
      if (!locationData) return { imageIdentifiers: [] }
      const requestData: LocationDrawImagesRequest = {
        title: locationData.title,
        description: locationData.description,
        emoji: locationData.emoji,
        placeSearch: locationData.placeSearch ?? locationData.title,
      }
      if (category === 'places') {
        const result = await getPlaceDrawDetails(requestData)
        return {
          imageIdentifiers: result.imageUrls ?? [],
          rating: result.rating,
          userRatingCount: result.userRatingCount,
          category: result.category,
        }
      }
      if (category === 'locations') {
        const result = await getLocationDrawImages(requestData)
        return {
          imageIdentifiers: result.imageIds ?? result.imageUrls ?? [],
        }
      }
      return { imageIdentifiers: [] }
    },
    enabled: !!locationData,
  })
}
